import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import { createUnit, getAllUnits, updateUnit } from "../../redux/unitSlice";
import AppTable from "../../common/AppTable/AppTable";
import AddBtn from "../../common/AppSpecificConpnents/AddBtn";
import { showSnackbar } from "../../redux/snackbarSlice";
import { SortTableData } from "../../common/AppTable/SortTableData";
import {
  SaveRowButton,
  CancelRowButton,
  EditRowButton,
  DeleteRowButton,
} from "../../common/Buttons/TableButtons";
import { buildPermissionContext } from "../../util/permissions";

const UnitForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;
  const isEditMode = location.state?.editMode || false;

  const [rowEditIndex, setRowEditIndex] = useState(null);
  const [newRow, setNewRow] = useState(null);
  const [editedRow, setEditedRow] = useState(null);
  const [invalidUnits, setInvalidUnits] = useState([]);

  const existingUnit = useMemo(() => {
    return location.state?.unit || {};
  }, [location.state?.unit]);

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const tableRef = useRef();
  const [localSortConfig, setLocalSortConfig] = useState({
    key: null,
    direction: null,
  });

  const [unitData, setUnitData] = useState({
    unit_type_id: existingUnit?.unit_type_id || null,
    unit_type_name: existingUnit?.unit_type_name || "",
    unit_type_for_unit:
      structuredClone(
        existingUnit?.unit_type_for_name?.map((u) => ({ ...u, existing: true }))
      ) || [],
  });

  const [sortedUnitType, setSortedUnitTypes] = useState([]);

  useEffect(() => {}, [unitData, localSortConfig]);

  useEffect(() => {
    if (isEditMode && existingUnit) {
      setUnitData({
        unit_type_id: existingUnit?.unit_type_id || null,
        unit_type_name: existingUnit?.unit_type_name || "",
        unit_type_for_unit:
          existingUnit?.unit_type_for_name?.map((u) => ({
            ...u,
            existing: true,
          })) || [],
      });
    }
  }, [isEditMode, existingUnit]);

  const handleChange = (e) => {
    setUnitData({ ...unitData, [e.target.name]: e.target.value });
  };

  const handleDefaultUnitChange = (index) => {
    if (index === rowEditIndex && editedRow) {
      setEditedRow((prev) => ({
        ...prev,
        is_default_unit_for_type: true,
      }));
    }

    setUnitData((prevData) => ({
      ...prevData,
      unit_type_for_unit: prevData.unit_type_for_unit.map((unit, i) => ({
        ...unit,
        is_default_unit_for_type: i === index,
      })),
    }));
  };

  const handleEditedUnitChange = (field, value) => {
    setEditedRow((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddUnit = () => {
    setNewRow({
      unit_name: "",
      is_default_unit_for_type: false,
    });
  };

  const handleSaveUnitRow = () => {
    if (newRow.unit_name.length === 0) {
      snackbar("Please enter unit name.", "error");
      return;
    } else if (
      unitData?.unit_type_for_unit?.find(
        (u) =>
          u.unit_name.trim().toLowerCase() ===
          newRow.unit_name?.trim()?.toLowerCase()
      )
    ) {
      snackbar("Unit is already exists.", "error");
      return;
    }
    setUnitData((prev) => {
      if (newRow.is_default_unit_for_type) {
        prev.unit_type_for_unit.forEach(
          (u) => (u.is_default_unit_for_type = false)
        );
      }
      const data = {
        ...prev,
        unit_type_for_unit: [newRow, ...prev.unit_type_for_unit],
      };
      return data;
    });
    setNewRow(null);
  };

  const handleEditRow = (index) => {
    if (!("unit_name" in editedRow) || editedRow?.unit_name?.length === 0) {
      snackbar("Please enter unit name.", "error");
      return;
    }
    const updatedUnits = [...unitData.unit_type_for_unit];
    updatedUnits[index] = editedRow;
    setUnitData((prev) => ({
      ...prev,
      unit_type_for_unit: updatedUnits,
    }));
    setRowEditIndex(null);
    setEditedRow(null);
  };

  const handleCancelEdit = () => {
    if (!editedRow.existing) {
      setUnitData((prev) => {
        prev.unit_type_for_unit.splice(rowEditIndex, 1);
        return prev;
      });
    }
    setRowEditIndex(null);
    setEditedRow(null);
  };

  const handleDeleteUnit = (index) => {
    setUnitData((prevData) => ({
      ...prevData,
      unit_type_for_unit: prevData.unit_type_for_unit.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handleSave = async () => {
    if (unitData.unit_type_name?.trim().length === 0) {
      snackbar("Please enter unit type name", "error");
      return;
    } else if (unitData?.unit_type_for_unit.length === 0) {
      snackbar("Please add at least single unit.", "error");
      return;
    }
    let resp = {};
    const data = {
      ...unitData,
      unit_type_name: unitData.unit_type_name?.trim(),
    };
    if (isEditMode) {
      resp = await dispatch(updateUnit(data));
    } else {
      delete unitData.unit_type_id;
      resp = await dispatch(createUnit(data));
    }

    if (resp.payload.success) {
      snackbar(resp.payload.message, "success");
    } else {
      snackbar(resp.payload.error, "error");
      if ("duplicate_units" in resp.payload) {
        setInvalidUnits(resp.payload.duplicate_units);
      }
      return;
    }

    dispatch(getAllUnits({}));
    navigate("/units");
  };

  useEffect(() => {
    if (localSortConfig.key === null) {
      setSortedUnitTypes(unitData.unit_type_for_unit);
      return;
    } else {
      const dt = SortTableData(
        unitData.unit_type_for_unit,
        localSortConfig.direction,
        localSortConfig.key
      );
      setSortedUnitTypes(dt);
    }
  }, [unitData, localSortConfig]);
  const setDefuUnit =
    isEditMode &&
    existingUnit?.unit_type_for_name?.find(
      (u) => u.is_default_unit_for_type === true
    )
      ? true
      : false;
  return (
    <div>
      <AppBar
        title={
          isEditMode
            ? "Edit Units for " + existingUnit?.unit_type_name
            : "New Unit Type"
        }
        showBlackTitle={false}
        buttons={[
          {
            label: "Save",
            onClick: canEdit ? handleSave : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save unit types."
              : undefined,
          },
        ]}
        showTablesDropdown={false}
        showProfileButton={false}
      />
      <form style={{ marginBottom: "20px" }}>
        <label style={{ display: "inline" }}>Unit Type Name</label>
        <input
          style={{ width: "300px", display: "inline", marginLeft: "10px" }}
          type="text"
          name="unit_type_name"
          value={unitData.unit_type_name}
          onChange={handleChange}
        />
      </form>

      <AppTable
        ref={tableRef}
        columnsData={[
          { headName: "Unit", field: "unit_name", required: true, sort: true },
          {
            headName: "Default Unit",
            field: "is_default_unit_for_type",
            sort: true,
          },
          { headName: "Action", field: "action", textCenter: true },
        ]}
        showSearchInput={false}
        paginate={false}
        setLocalSortConfig={setLocalSortConfig}
        extrapadding="0px"
      >
        {newRow && (
          <tr>
            <td>
              <input
                type="text"
                value={newRow.unit_name}
                onChange={(e) =>
                  setNewRow((prev) => ({ ...prev, unit_name: e.target.value }))
                }
              />
            </td>

            <td style={{ width: "100px", textAlign: "center" }}>
              <label className="checkbox-label">
                <input
                  type="radio"
                  disabled={setDefuUnit}
                  checked={newRow.is_default_unit_for_type}
                  onChange={(e) =>
                    setNewRow((prev) => ({
                      ...prev,
                      is_default_unit_for_type: true,
                    }))
                  }
                />
                <span style={{ marginLeft: "-35px" }}>Yes</span>
              </label>
            </td>

            <td style={{ width: "100px" }}>
              <div className="app-action-btns">
                <SaveRowButton onClick={handleSaveUnitRow} />
                <CancelRowButton onClick={() => setNewRow(null)} />
              </div>
            </td>
          </tr>
        )}
        {sortedUnitType.map((unit, index) => {
          const isEditing = rowEditIndex === index;
          const restrictEditDel =
            (setDefuUnit && unit.is_default_unit_for_type) ||
            unit.used_in_parts_required === true ||
            unit.used_in_text_box_content === true
              ? true
              : false;

          const styleObj = {
            border: "1px solid #f405056b",
            borderRadius: "5px",
            padding: "3px 5px",
          };
          return (
            <tr key={index}>
              <td>
                <div
                  style={
                    invalidUnits.find(
                      (u) =>
                        unit.unit_name?.trim()?.toLowerCase() ===
                        u?.trim()?.toLowerCase()
                    )
                      ? styleObj
                      : {}
                  }
                >
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedRow.unit_name}
                      onChange={(e) =>
                        handleEditedUnitChange("unit_name", e.target.value)
                      }
                    />
                  ) : (
                    unit.unit_name
                  )}
                </div>
              </td>

              <td style={{ width: "100px", textAlign: "center" }}>
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="defaultUnit"
                    checked={unit.is_default_unit_for_type}
                    onChange={() => handleDefaultUnitChange(index)}
                    disabled={!isEditing || setDefuUnit ? true : false}
                  />
                  <span style={{ marginLeft: "-35px" }}>Yes</span>
                </label>
              </td>

              <td style={{ width: "100px" }}>
                {isEditing ? (
                  <div className="app-action-btns">
                    <SaveRowButton onClick={() => handleEditRow(index)} />
                    <CancelRowButton onClick={handleCancelEdit} />
                  </div>
                ) : (
                  <div className="app-action-btns">
                    <EditRowButton
                      disabled={restrictEditDel}
                      onClick={() => {
                        setRowEditIndex(index);
                        setEditedRow(unit);
                      }}
                    />
                    <DeleteRowButton
                      onClick={() => handleDeleteUnit(index)}
                      disabled={restrictEditDel}
                    />
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </AppTable>

      <div style={{ marginTop: "10px" }}>
        <AddBtn onClick={handleAddUnit} />
      </div>
    </div>
  );
};

export default UnitForm;
